import type { BillingDto, BillingPlan, TaxMode } from '@af/shared';
import { PLAN_LIMITS } from '@af/shared';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/ui/brand-logo';
import { Button, Spinner } from '@/components/ui/button';
import { IconCheck } from '@/components/ui/icons';
import { formatEuro } from '@/lib/plan-pricing';
import { limitLines, PLAN_NAMES } from './billing-plans';

/** Same key as the Home redirect: "skip" means the guided setup is not forced on this browser session. */
const ONBOARDING_REDIRECT_KEY = 'af:onboarding-redirected';
const STEPS = ['provider', 'agent', 'hello'] as const;

export function skipOnboarding(): void {
  try {
    globalThis.sessionStorage?.setItem(ONBOARDING_REDIRECT_KEY, '1');
  } catch {}
  window.location.assign('/');
}

/** Shown right after Checkout while the webhook has not confirmed the subscription yet. */
export function ConfirmingPayment() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="mx-auto flex w-full max-w-md flex-col items-center gap-3 rounded-xl border border-border bg-bg p-8 text-center"
    >
      <Spinner />
      <h1 className="text-lg font-medium">{t('billing.success.confirmingTitle')}</h1>
      <p className="text-sm text-muted">{t('billing.success.confirmingText')}</p>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className="text-sm font-medium sm:text-right">{children}</dd>
    </div>
  );
}

/**
 * Thank-you + receipt summary after a successful Checkout, followed by the guided setup (skippable).
 * Everything shown comes from the billing status and the Stripe prices; nothing is invented.
 */
export function CheckoutSuccess({
  b,
  price,
  taxMode,
  email,
  orgName,
  locale,
  busy,
  onManage,
}: {
  b: BillingDto;
  price: BillingPlan | undefined;
  taxMode: TaxMode;
  email: string;
  orgName: string;
  locale: string;
  busy: boolean;
  onManage: () => void;
}) {
  const { t } = useTranslation();
  const plan = b.plan ?? 'starter';
  const planName = PLAN_NAMES[plan];
  const yearly = b.interval === 'year';
  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  const amount = price ? formatEuro(price.amount, locale) : null;
  const trialing = b.status === 'trialing' && !!b.trialEnd;
  const vat = t(taxMode === 'kor' ? 'billing.noVat' : 'billing.exVat');
  const lines = limitLines(b.limits ?? PLAN_LIMITS[plan], locale, t);
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-8">
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success">
          <IconCheck size={26} />
        </span>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{t('billing.success.title')}</h1>
        <p className="max-w-xl text-sm text-muted">
          {t(trialing ? 'billing.success.subtitleTrial' : 'billing.success.subtitle', {
            plan: planName,
            email,
          })}
        </p>
      </div>

      <section
        className="rounded-xl border border-border bg-bg p-5"
        aria-label={t('billing.success.receipt')}
      >
        <h2 className="text-sm font-medium">{t('billing.success.receipt')}</h2>
        <dl className="mt-2 divide-y divide-border">
          <Row label={t('billing.success.plan')}>
            {planName} · {t(yearly ? 'billing.intervalYear' : 'billing.intervalMonth')}
          </Row>
          <Row label={t('billing.success.company')}>{orgName}</Row>
          <Row label={t('billing.success.billedTo')}>{email}</Row>
          {trialing && b.trialEnd && (
            <Row label={t('billing.success.trial')}>
              {t('billing.success.trialValue', { date: date(b.trialEnd) })}
            </Row>
          )}
          <Row label={t('billing.success.today')}>
            {formatEuro(trialing ? 0 : (price?.amount ?? 0), locale)}
          </Row>
          {amount && (
            <Row label={t(trialing ? 'billing.success.firstPayment' : 'billing.success.renews')}>
              {trialing && b.trialEnd
                ? t('billing.success.firstPaymentValue', { amount, date: date(b.trialEnd) })
                : b.paidThrough
                  ? t('billing.success.renewsValue', { date: date(b.paidThrough) })
                  : amount}
              <span className="block text-xs font-normal text-muted">
                {t(yearly ? 'billing.success.thenYear' : 'billing.success.thenMonth', { amount })} · {vat}
              </span>
            </Row>
          )}
          <Row label={t('billing.success.included')}>
            <ul className="flex flex-col gap-0.5 font-normal">
              {lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </Row>
        </dl>
        <p className="mt-2 text-xs text-muted">
          {trialing && b.trialEnd
            ? t('billing.success.cancelNote', { date: date(b.trialEnd) })
            : t('billing.success.invoiceNote')}
        </p>
        {b.canManage && (
          <Button className="mt-4" size="sm" disabled={busy} onClick={onManage}>
            {t('billing.manage')}
          </Button>
        )}
      </section>

      <section
        className="rounded-xl border border-accent/40 bg-bg p-5"
        aria-label={t('billing.success.nextTitle')}
      >
        <h2 className="text-lg font-medium">{t('billing.success.nextTitle')}</h2>
        <p className="mt-1 text-sm text-muted">{t('billing.success.nextSubtitle')}</p>
        <ol className="mt-4 flex flex-col gap-3">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-panel-2 text-xs font-medium">
                {i + 1}
              </span>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{t(`billing.success.steps.${s}.title`)}</span>
                <span className="text-sm text-muted">{t(`billing.success.steps.${s}.desc`)}</span>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <a
            href="/onboarding"
            className="inline-flex h-10 items-center rounded-lg bg-cta px-5 text-sm font-medium text-cta-fg hover:bg-cta-hover"
          >
            {t('billing.success.start')}
          </a>
          <Button variant="ghost" onClick={skipOnboarding}>
            {t('billing.success.skip')}
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted">{t('billing.success.skipHint')}</p>
      </section>
      <div className="flex justify-center">
        <Logo size={20} />
      </div>
    </div>
  );
}
